import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { communicationsApi } from "@/api/communications";
import type {
  AddParticipantDto,
  CommunicationLogEntryDto,
  CommunicationLogQuery,
  CreateCommunicationLogDto,
  PlacementRefDto,
  UpdateCommunicationLogDto,
} from "@/api/communications";

/**
 * Communication Log v1.0 §8 — React Query hooks. Mutations invalidate
 * every cache key family that could be affected: the inbox list, the
 * entry detail, every owner-scoped feed for any placement on the entry,
 * every recent-count badge for those same owners, and the dashboard's
 * "my-recent" widget. The cascading invalidation is necessary because a
 * single edit can touch all of those views simultaneously.
 */

export const communicationKeys = {
  all: ["communications"] as const,
  list: (query?: CommunicationLogQuery) =>
    ["communications", "list", query] as const,
  detail: (id: string) => ["communications", "detail", id] as const,
  activity: (id: string) => ["communications", id, "activity"] as const,
  byOwner: (ownerType: string, ownerId: string) =>
    ["communications", "by-owner", ownerType, ownerId] as const,
  recentCount: (ownerType: string, ownerId: string, windowDays: number) =>
    [
      "communications",
      "recent-count",
      ownerType,
      ownerId,
      windowDays,
    ] as const,
  myRecent: (limit: number) =>
    ["communications", "my-recent", limit] as const,
  contactSearch: (q: string, limit: number) =>
    ["communications", "contacts", q, limit] as const,
};

function invalidateForEntry(
  qc: QueryClient,
  entry: CommunicationLogEntryDto | undefined,
) {
  // The blunt instrument: invalidate every "communications" key. Keeps
  // every list, detail, by-owner feed, recent-count badge, and dashboard
  // widget in sync without enumerating placement owners.
  void qc.invalidateQueries({ queryKey: communicationKeys.all });
  if (entry) {
    void qc.invalidateQueries({ queryKey: communicationKeys.detail(entry.id) });
  }
}

export function useCommunications(query: CommunicationLogQuery) {
  return useQuery({
    queryKey: communicationKeys.list(query),
    queryFn: () => communicationsApi.list(query),
  });
}

export function useCommunication(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? communicationKeys.detail(id)
      : ["communications", "detail", "missing"],
    queryFn: () => communicationsApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useCreateCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCommunicationLogDto) =>
      communicationsApi.create(payload),
    onSuccess: (entry) => invalidateForEntry(qc, entry),
  });
}

export function useUpdateCommunication(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCommunicationLogDto) =>
      communicationsApi.update(id, payload),
    onSuccess: (entry) => invalidateForEntry(qc, entry),
  });
}

export function useDeleteCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.delete(id),
    onSuccess: () => invalidateForEntry(qc, undefined),
  });
}

export function useRestoreCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.restore(id),
    onSuccess: () => invalidateForEntry(qc, undefined),
  });
}

export function useAddPlacement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlacementRefDto) =>
      communicationsApi.addPlacement(id, payload),
    onSuccess: (entry) => invalidateForEntry(qc, entry),
  });
}

export function useRemovePlacement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { ownerType: string; ownerId: string }) =>
      communicationsApi.removePlacement(id, vars.ownerType, vars.ownerId),
    onSuccess: (entry) => invalidateForEntry(qc, entry),
  });
}

export function useAddParticipant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddParticipantDto) =>
      communicationsApi.addParticipant(id, payload),
    onSuccess: (entry) => invalidateForEntry(qc, entry),
  });
}

export function useRemoveParticipant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) =>
      communicationsApi.removeParticipant(id, participantId),
    onSuccess: (entry) => invalidateForEntry(qc, entry),
  });
}

export function useCommunicationActivity(
  id: string | undefined,
  skip = 0,
  take = 50,
) {
  return useQuery({
    queryKey: id
      ? [...communicationKeys.activity(id), skip, take]
      : ["communications", "activity", "missing"],
    queryFn: () => communicationsApi.getActivity(id!, skip, take),
    enabled: Boolean(id),
  });
}

export function useCommunicationsByOwner(
  ownerType: string | undefined,
  ownerId: string | undefined,
  skip = 0,
  take = 50,
) {
  return useQuery({
    queryKey:
      ownerType && ownerId
        ? [...communicationKeys.byOwner(ownerType, ownerId), skip, take]
        : ["communications", "by-owner", "missing"],
    queryFn: () => communicationsApi.listByOwner(ownerType!, ownerId!, skip, take),
    enabled: Boolean(ownerType && ownerId),
  });
}

export function useCommunicationsRecentCount(
  ownerType: string | undefined,
  ownerId: string | undefined,
  windowDays = 30,
) {
  return useQuery({
    queryKey:
      ownerType && ownerId
        ? communicationKeys.recentCount(ownerType, ownerId, windowDays)
        : ["communications", "recent-count", "missing"],
    queryFn: () =>
      communicationsApi.recentCountByOwner(ownerType!, ownerId!, windowDays),
    enabled: Boolean(ownerType && ownerId),
  });
}

export function useMyRecentCommunications(limit = 5) {
  return useQuery({
    queryKey: communicationKeys.myRecent(limit),
    queryFn: () => communicationsApi.myRecent(limit),
  });
}

/**
 * Drives the contact-picker autocomplete in <LogCommunicationModal>. A
 * <c>null</c> query disables the request so an empty input doesn't
 * flood the endpoint. Caller debounces (250ms) before calling.
 */
export function useContactSearch(query: string | null, limit = 10) {
  return useQuery({
    queryKey: communicationKeys.contactSearch(query ?? "", limit),
    queryFn: () => communicationsApi.searchContacts(query ?? "", limit),
    enabled: query !== null,
    staleTime: 30_000,
  });
}
